/*  Chi sta usando l'applicativo, chiesto al server.

    Prima qui c'era una costante: l'identificativo di una persona, cablato nel codice o
    passato in una variabile di build. Ogni chiamata di scrittura lo mandava nel corpo, e
    il server ci credeva. Ora l'identità la stabilisce il server e questo modulo la
    legge — insieme all'elenco di cosa quella persona può fare.

    I permessi non si ricalcolano da questo lato. Se il frontend sapesse che «l'ufficio
    può emettere ordini» ci sarebbero due elenchi da tenere allineati a mano, e il giorno
    in cui divergono l'interfaccia mostra un bottone che il server rifiuta: il modo
    peggiore di scoprirlo. Qui arriva un elenco di stringhe e si guarda se contiene
    quello che serve.

    `puo()` serve a NON mostrare comandi inutilizzabili, non a proteggere niente: il
    controllo che conta è quello del server, e resta lì anche se qualcuno cambia
    l'interfaccia dagli strumenti del browser.
*/

import { api } from '../api/client';

// Riesportate da qui perché è questo il modulo che parla di identità: il file separato
// esiste solo per non creare un ciclo con il client HTTP.
export { dichiaraUtente, utenteDichiarato } from './identitaDichiarata';

/*  I nomi arrivano dall'enum Permesso del backend. Sono ripetuti qui — è la sola cosa
    duplicata fra i due lati — perché servono a TypeScript per rifiutare un permesso
    scritto male: `puo(s, 'EmettiOrdine')` senza la «i» finale sarebbe altrimenti un
    controllo sempre falso, cioè un bottone che non compare mai e nessun errore da
    nessuna parte. Un nome cambiato nel backend fa fallire la compilazione qui.  */
export type Permesso =
  | 'VediPreventivi'
  | 'RedigiPreventivi'
  | 'RiapriPreventiviConfermati'
  | 'VediCommesse'
  | 'VediCosti'
  | 'GestisciCommesse'
  | 'FatturaCommesse'
  | 'VediRichieste'
  | 'CreaRichieste'
  | 'InviaRichieste'
  | 'AnnullaRichieste'
  | 'VediOrdini'
  | 'EmettiOrdini'
  | 'InviaOrdini'
  | 'ConfermaOrdini'
  | 'AnnullaOrdini'
  | 'VediCarichi'
  | 'RegistraCarichi'
  | 'AnnullaCarichi'
  | 'VediArticoli'
  | 'ImportaListini'
  | 'CompilaRapportini'
  | 'VediRapportini'
  | 'ApprovaRapportini'
  | 'VediAnagrafiche'
  | 'GestisciUtenti'
  | 'VediRegistro'
  | 'GestisciTestiStampa'
  | 'GestisciPredefinitiPreventivo'
  | 'EmettiScontrini'
  | 'GestisciCassa';

export interface Sessione {
  autenticato: boolean;
  id: string | null;
  codice: string | null;
  nomeCompleto: string | null;
  ruolo: string | null;
  codiceSede: string | null;
  permessi: string[];
  /** Vero quando l'identità la dichiara il chiamante: va detto a schermo, non nascosto. */
  identitaProvvisoria: boolean;
  /** Si entra con codice e password: senza sessione va mostrata la schermata d'accesso. */
  servePassword: boolean;
  /** La password è provvisoria: prima di tutto il resto va cambiata. */
  deveCambiarePassword: boolean;
  /** La posta è configurata: la schermata d'accesso può offrire «password dimenticata». */
  postaCollegata: boolean;
}

export interface UtenteScelto {
  id: string;
  codice: string;
  nomeCompleto: string;
  email: string | null;
  ruolo: string;
  codiceSede: string | null;
  attivo: boolean;
  deveCambiarePassword: boolean;
  // Il backend li calcola dal ruolo e li serializza: dichiararli tiene allineati i due
  // lati, ed è la ragione per cui il controllo dei contratti se n'è accorto.
  permessi: string[];
}

export const ANONIMO: Sessione = {
  autenticato: false,
  id: null,
  codice: null,
  nomeCompleto: null,
  ruolo: null,
  codiceSede: null,
  permessi: [],
  identitaProvvisoria: false,
  servePassword: false,
  deveCambiarePassword: false,
  postaCollegata: false,
};

/*  La sessione, disponibile anche senza rete.

    Serve perché il rapportino di lavoro si compila in un cantiere senza campo, e porta
    dentro l'identificativo di chi ha lavorato: senza sapere chi è collegato non si può
    nemmeno cominciare a compilarlo. Quindi la sessione si tiene da parte quando arriva,
    con lo stesso criterio dell'elenco delle commesse in cantiere.

    La copia salvata NON è una credenziale e non autorizza niente: quando la rete torna,
    la sincronizzazione passa dal server, che decide di nuovo chi sei e cosa puoi fare.
    Un ruolo cambiato ieri vale al primo collegamento, non alla scadenza di qualcosa.  */
const CACHE = 'teraworks.sessione';

export async function sessioneCorrente(): Promise<{ sessione: Sessione; dallaCache: boolean }> {
  try {
    const sessione = await api.get<Sessione>('/sessione');
    if (sessione.autenticato) scriviCache(sessione);
    return { sessione, dallaCache: false };
  } catch (errore) {
    const salvata = leggiCache();

    /*  Si rilancia l'errore originale e non uno generico: «non risulti collegato» e «il
        server non ha risposto» sono due cose diverse, e chi guarda lo schermo deve
        leggere la seconda quando è la seconda. Con un messaggio inventato qui, un'API
        non ancora avviata diventava un problema di utenza — ed è costato dieci minuti di
        ricerca nel posto sbagliato la prima volta che è capitato.  */
    if (salvata === null) throw errore;

    return { sessione: salvata, dallaCache: true };
  }
}

function leggiCache(): Sessione | null {
  try {
    const salvato = window.localStorage.getItem(CACHE);
    if (!salvato) return null;

    const letto = JSON.parse(salvato) as Partial<Sessione>;
    // Una copia scritta da una versione precedente può non avere i campi che servono:
    // meglio ripartire dalla rete che credere a un oggetto a metà.
    if (typeof letto.id !== 'string' || !Array.isArray(letto.permessi)) return null;

    return { ...ANONIMO, ...letto } as Sessione;
  } catch {
    return null;
  }
}

function scriviCache(sessione: Sessione) {
  try {
    window.localStorage.setItem(CACHE, JSON.stringify(sessione));
  } catch {
    /* niente: sarà il prossimo avvio senza rete a non trovare la copia */
  }
}

/** Le persone fra cui scegliere in sviluppo. Fuori dallo sviluppo la rotta non esiste. */
export const utentiDisponibili = () => api.get<UtenteScelto[]>('/sessione/utenti');

export interface EsitoDellAccesso {
  nomeCompleto: string;
  ruolo: string;
  deveCambiarePassword: boolean;
}

/*  Entrare, uscire, cambiare la propria password.

    Non c'è niente da salvare dopo l'accesso: la sessione sta in un cookie che il browser
    rimanda da solo e che questo codice non può nemmeno leggere. È il motivo per cui qui
    non compare nessun token — quello che non si può leggere non si può nemmeno far
    rubare da uno script.  */
export const entra = (codice: string, password: string) =>
  api.post<EsitoDellAccesso>('/sessione/accesso', { codice, password });

export const esci = () => api.post<void>('/sessione/uscita', {});

export const cambiaPassword = (vecchia: string, nuova: string) =>
  api.post<void>('/sessione/password', { vecchia, nuova });

/*  La copia salvata della sessione va buttata quando si esce. Senza, la schermata
    d'accesso resterebbe disegnata sopra i dati del collega precedente appena la rete
    manca per un istante — ed è il caso in cui la copia esiste apposta.  */
export function dimenticaSessione(): void {
  try {
    window.localStorage.removeItem(CACHE);
  } catch {
    /* niente da dimenticare */
  }
}

export const puo = (sessione: Sessione, permesso: Permesso): boolean =>
  sessione.permessi.includes(permesso);
